/**
 * CR313: Feature Flags Admin Hook
 * 
 * Manages loading, dirty state, and persistence for feature flags.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  FEATURE_FLAGS_REGISTRY,
  getRegistryKeys,
  isRegisteredFlag,
  type FeatureFlagRegistryEntry,
} from '@/lib/admin/featureFlagsRegistry';

export interface FeatureFlagState {
  key: string;
  registryEntry: FeatureFlagRegistryEntry | null;
  storedValue: unknown | null; // null = no override in DB
  effectiveValue: unknown;
  isOverridden: boolean;
}

export interface UseFeatureFlagsAdminResult {
  flags: FeatureFlagState[];
  unregisteredFlags: Array<{ key: string; value: unknown }>;
  loading: boolean;
  error: string | null;
  draftValues: Map<string, unknown>;
  isDirty: boolean;
  changedKeys: string[];
  updateDraft: (key: string, value: unknown) => void;
  resetDraft: () => void;
  save: () => Promise<boolean>;
  deleteOverride: (key: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (typeof a !== 'object') return false;
  
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  
  if (aKeys.length !== bKeys.length) return false;
  
  for (const key of aKeys) {
    if (!bKeys.includes(key)) return false;
    if (!deepEqual(aObj[key], bObj[key])) return false;
  }
  
  return true;
}

export function useFeatureFlagsAdmin(): UseFeatureFlagsAdminResult {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dbOverrides, setDbOverrides] = useState<Map<string, unknown>>(new Map());
  const [unregisteredFlags, setUnregisteredFlags] = useState<Array<{ key: string; value: unknown }>>([]);
  const [draftValues, setDraftValues] = useState<Map<string, unknown>>(new Map());
  
  // Load flags from DB
  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const registryKeys = getRegistryKeys();
      
      // Fetch all app_config rows
      const { data, error: fetchError } = await supabase
        .from('app_config')
        .select('key, value_json');
      
      if (fetchError) throw fetchError;
      
      const overrides = new Map<string, unknown>();
      const unregistered: Array<{ key: string; value: unknown }> = [];
      
      for (const row of data || []) {
        if (registryKeys.includes(row.key)) {
          overrides.set(row.key, row.value_json);
        } else {
          unregistered.push({ key: row.key, value: row.value_json });
        }
      }
      
      setDbOverrides(overrides);
      setUnregisteredFlags(unregistered.sort((a, b) => a.key.localeCompare(b.key)));
      
      // Initialize draft to current effective values
      const initialDraft = new Map<string, unknown>();
      for (const entry of FEATURE_FLAGS_REGISTRY) {
        const effectiveValue = overrides.has(entry.key) 
          ? overrides.get(entry.key) 
          : entry.defaultValue;
        initialDraft.set(entry.key, effectiveValue);
      }
      setDraftValues(initialDraft);
      
    } catch (err) {
      console.error('[FeatureFlagsAdmin] Load error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadFlags();
  }, [loadFlags]);
  
  // Build flags list
  const flags = useMemo<FeatureFlagState[]>(() => {
    return FEATURE_FLAGS_REGISTRY.map(entry => {
      const storedValue = dbOverrides.has(entry.key) ? dbOverrides.get(entry.key) : null;
      const effectiveValue = storedValue !== null ? storedValue : entry.defaultValue;
      
      return {
        key: entry.key,
        registryEntry: entry,
        storedValue,
        effectiveValue,
        isOverridden: storedValue !== null,
      };
    });
  }, [dbOverrides]);
  
  // Compute dirty state
  const { isDirty, changedKeys } = useMemo(() => {
    const changed: string[] = [];
    
    for (const entry of FEATURE_FLAGS_REGISTRY) {
      const effectiveValue = dbOverrides.has(entry.key) 
        ? dbOverrides.get(entry.key) 
        : entry.defaultValue;
      const draftValue = draftValues.get(entry.key);
      
      if (!deepEqual(effectiveValue, draftValue)) {
        changed.push(entry.key);
      }
    }
    
    return { isDirty: changed.length > 0, changedKeys: changed };
  }, [dbOverrides, draftValues]);
  
  const updateDraft = useCallback((key: string, value: unknown) => {
    setDraftValues(prev => {
      const next = new Map(prev);
      next.set(key, value);
      return next;
    });
  }, []);
  
  const resetDraft = useCallback(() => {
    const resetMap = new Map<string, unknown>();
    for (const entry of FEATURE_FLAGS_REGISTRY) {
      const effectiveValue = dbOverrides.has(entry.key) 
        ? dbOverrides.get(entry.key) 
        : entry.defaultValue;
      resetMap.set(entry.key, effectiveValue);
    }
    setDraftValues(resetMap);
  }, [dbOverrides]);
  
  const save = useCallback(async (): Promise<boolean> => {
    if (changedKeys.length === 0) return true;
    
    try {
      // Upsert each changed key
      for (const key of changedKeys) {
        const value = draftValues.get(key);
        
        const { error: upsertError } = await supabase
          .from('app_config')
          .upsert({ 
            key, 
            value_json: value as import('@/integrations/supabase/types').Json,
            updated_at: new Date().toISOString(),
          });
        
        if (upsertError) throw upsertError;
      }
      
      toast({
        title: 'Flags saved',
        description: `Updated ${changedKeys.length} flag${changedKeys.length > 1 ? 's' : ''}`,
      });
      
      // Reload to sync state
      await loadFlags();
      return true;
      
    } catch (err) {
      console.error('[FeatureFlagsAdmin] Save error:', err);
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  }, [changedKeys, draftValues, loadFlags, toast]);
  
  const deleteOverride = useCallback(async (key: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('app_config')
        .delete()
        .eq('key', key);
      
      if (deleteError) throw deleteError;
      
      toast({
        title: 'Override deleted',
        description: `${key} reverted to default`,
      });
      
      await loadFlags();
      return true;
      
    } catch (err) {
      console.error('[FeatureFlagsAdmin] Delete error:', err);
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  }, [loadFlags, toast]);
  
  return {
    flags,
    unregisteredFlags,
    loading,
    error,
    draftValues,
    isDirty,
    changedKeys,
    updateDraft,
    resetDraft,
    save,
    deleteOverride,
    refresh: loadFlags,
  };
}
